import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../../components/layout';
import StatCard from '../../components/ui/StatCard/StatCard';
import { TypographyH1, TypographyMuted } from '../../components/ui/Typography';
import { Icon } from '../../components/ui/Icon';
import { Button } from '../../components/ui/Button2';
import { useConfirm } from '../../components/ui/AlertDialog';
import CreateProjectModal from './components/CreateProjectModal';
import EditProjectModal from './components/EditProjectModal';
import ProjectsTable from './components/ProjectsTable';
import {
  listSessions,
  createSession,
  updateSession,
  deleteSession,
} from '../../api/sessions';
import styles from './ProjectsListPage.module.css';

const ProjectsListPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const { confirm, confirmDialog } = useConfirm();
  const navigate = useNavigate();

  // Fetch projects (sessions) from backend
  const fetchProjects = async () => {
    try {
      setLoading(true);
      const data = await listSessions();
      setProjects(data.sessions || []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch projects');
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  // Handle project creation
  const handleCreateProject = async (projectData) => {
    try {
      const newProject = await createSession(projectData);
      setProjects(prev => [newProject, ...prev]);
      setShowCreateModal(false);

      // Navigate to the project workspace
      navigate(`/project/${newProject.session_id}`);
    } catch (err) {
      console.error('Error creating project:', err);
      throw err;
    }
  };

  // Handle project edit — open modal with current project data
  const handleEditProject = (projectId) => {
    const project = projects.find(p => p.session_id === projectId);
    if (project) {
      setEditingProject(project);
    }
  };

  // Handle project save after editing
  const handleSaveProject = async (projectId, updateData) => {
    try {
      const updatedSession = await updateSession(projectId, updateData);
      setProjects(prev => prev.map(p =>
        p.session_id === projectId ? { ...p, ...updatedSession } : p
      ));
      setEditingProject(null);
    } catch (err) {
      throw new Error(err.detail || err.message || 'Failed to update project');
    }
  };

  // The row action requests the delete; useConfirm shows the dialog and
  // resolves before we hit the API.
  const handleDeleteProject = async (projectId) => {
    const project = projects.find(p => p.session_id === projectId);
    const ok = await confirm({
      title: 'Delete project?',
      description: `This permanently deletes ${
        project?.analysis_name ? `"${project.analysis_name}"` : 'this project'
      } and cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
      media: <Icon name="delete" />,
    });
    if (!ok) return;
    try {
      await deleteSession(projectId);
      setProjects(prev => prev.filter(p => p.session_id !== projectId));
    } catch (err) {
      console.error('Error deleting project:', err);
      alert('Failed to delete project. Please try again.');
    }
  };

  // Stats read unfiltered totals — they stay accurate when the user
  // applies search / status filters inside the table.
  const stats = {
    total: projects.length,
    active: projects.filter(p => p.session_status === 'IN_PROGRESS').length,
    completed: projects.filter(p => p.session_status === 'COMPLETED').length
  };

  return (
    <PageLayout activeNavItem="projects" contentClassName={styles.wideContent} disableScroll>
      <div className={styles.projectsContainer}>
        {/* Header Section — title + subtitle only. Create CTA moved
            into the TableToolbar; search + status filter live in the
            DataTable's toolbar / FilterDrawer (Epic 11.4-projects). */}
        <div className={styles.headerSection}>
          <TypographyH1>Projects</TypographyH1>
          <TypographyMuted>
            Manage your automotive cybersecurity TARA projects
          </TypographyMuted>
        </div>

        {/* Stats Cards — read unfiltered totals */}
        <div className={styles.statsGrid}>
          <StatCard
            title="Total Projects"
            value={stats.total}
            subtitle="All projects"
            isLoading={loading}
          />
          <StatCard
            title="Active Projects"
            value={stats.active}
            subtitle="In progress"
            isLoading={loading}
          />
          <StatCard
            title="Completed"
            value={stats.completed}
            subtitle="Finished projects"
            isLoading={loading}
          />
        </div>

        {/* Projects Table — owns its own search + status filter via the
            TableToolbar header section and the FilterDrawer side panel. */}
        {error ? (
          <div className={styles.errorState}>
            <Icon name="error" size={48} className={styles.errorIcon} />
            <p>{error}</p>
            <Button
              variant="outline"
              size="sm"
              className={styles.retryButton}
              onClick={fetchProjects}
            >
              Retry
            </Button>
          </div>
        ) : (
          <ProjectsTable
            projects={projects}
            onEdit={handleEditProject}
            onDelete={handleDeleteProject}
            loading={loading}
            onCreateClick={() => setShowCreateModal(true)}
          />
        )}

        {/* Create Project Modal */}
        {showCreateModal && (
          <CreateProjectModal
            onClose={() => setShowCreateModal(false)}
            onCreate={handleCreateProject}
          />
        )}

        {/* Edit Project Modal */}
        {editingProject && (
          <EditProjectModal
            project={editingProject}
            onClose={() => setEditingProject(null)}
            onSave={handleSaveProject}
          />
        )}

        {confirmDialog}
      </div>
    </PageLayout>
  );
};

export default ProjectsListPage;
